import { type GID, type UnlikelyCollection, type UnlikelyPageInfo } from "@unlikelystudio/commerce-connector"
import { useSearch } from "@unlikelystudio/react-ecommerce-hooks"

import type { InferReturn, Nullish } from "~/@types/generics"
import { useLocale } from "~/lib/i18n/hooks/useLocale"
import { DEFAULT_COLLECTION_QUERY_PARAMS, DEFAULT_PAGE_INFO } from "~/lib/shopify/constants/collection-filters"
import { getSfCollection } from "~/lib/shopify/queries/get-sf-collection"
import { getPushesEditoInCurrentPage } from "~/lib/shopify/utils/get-collection-by-ids"
import { getIncludedFiltersByMarket } from "~/data/collection-default/get-included-filters-by-market"
import { getSfSortFromSortByParam } from "~/hooks/useShopifyCollectionProducts/utils/get-sf-sort-from-sort-param"
import type { fetchSbCollection } from "~/components/ui/Collection/_data/get"
import { serializeProductsAndPushesFromCurrentPage } from "~/components/ui/Collection/CollectionDefault/_utils/serialize-products-and-pushes-from-current-page"
import { PRODUCTS_PER_PAGE } from "~/components/ui/Collection/CollectionDefault/constants"
import { type TProductCard } from "~/components/ui/Product/ProductCard/_data/serializer"
import type { TPushEdito } from "~/components/ui/PushEdito/_data/serializer"
import { useSearchAndDiscoveryContext } from "~/providers/SearchAndDiscoveryProvider"
import { productFiltersToFacets } from "~/providers/SearchAndDiscoveryProvider/utils/product-filters-to-facets"

export type ShopifySearchFiltersData = {
  facets: InferReturn<typeof productFiltersToFacets>
  results: (TProductCard | TPushEdito)[]
  pageInfo: UnlikelyPageInfo | null
  currentPage: number
}

export function useShopifyCollectionProducts({
  id,
  sbCollection,
  initialData,
}: {
  id: Nullish<GID>
  sbCollection: InferReturn<typeof fetchSbCollection>["sbCollection"]
  initialData: ShopifySearchFiltersData
}) {
  const { defaultFilters, isFiltering, page, sortBy, productFilters } = useSearchAndDiscoveryContext()
  const locale = useLocale()

  const computedPageParam = initialData?.pageInfo
    ? { after: initialData?.pageInfo?.endCursor, pageNumber: initialData?.currentPage + 1 }
    : undefined

  const shouldDisplayInitialData = !isFiltering && sortBy === "default"

  return useSearch<
    ShopifySearchFiltersData,
    unknown,
    {
      id: Nullish<GID>
      defaultFilters: typeof defaultFilters
      page: typeof page
      sortBy: typeof sortBy
      productFilters: typeof productFilters
    }
  >({
    key: `shopifyCollectionPage-${id}-${locale}`,
    additionalQueryKeyParams: { id, defaultFilters, page, sortBy, productFilters },
    searchFunction: async ({ pageParam = {}, queryKey: [, { hitsPerPage }, { sortBy, id, productFilters }] }) => {
      const { productsSortKey, productReverse } = getSfSortFromSortByParam(sortBy)

      const currentPage = pageParam.pageNumber ?? 1

      const pushesEditoInCurrentPage = isFiltering ? [] : getPushesEditoInCurrentPage(sbCollection?.edito, currentPage)

      if (!id) {
        return {
          facets: [],
          pageInfo: DEFAULT_PAGE_INFO,
          results: [],
          currentPage,
        }
      }

      const response = await getSfCollection({
        id,
        locale,
        productsFirst: hitsPerPage - pushesEditoInCurrentPage.length,
        ...DEFAULT_COLLECTION_QUERY_PARAMS,
        ...(pageParam?.after && {
          productsAfter: pageParam.after,
        }),
        ...(pageParam?.before && {
          productsBefore: pageParam.before,
        }),
        productsFilters: productFilters ?? [],
        ...(productsSortKey && { productsSortKey }),
        ...(productReverse && { productReverse }),
      })?.catch((error) => {
        console.error(error)
        return {
          collection: {
            filters: [],
            products: [],
            productsPageInfo: DEFAULT_PAGE_INFO,
          },
        }
      })

      const { collection } = response ?? {}

      const { filters, productsPageInfo } = collection ?? {}

      // Serialiaze the product cards within the collection
      const [productCardsAndPushes, displayedFilters] = await Promise.all([
        serializeProductsAndPushesFromCurrentPage({
          isFiltering,
          products: collection?.products ?? [],
          pushesEditoInCurrentPage,
          currentPage,
          locale,
        }),
        getIncludedFiltersByMarket(collection as UnlikelyCollection, locale),
      ])

      // Serializing the facets
      const facets = productFiltersToFacets({
        defaultFilters: defaultFilters ?? [],
        filters: displayedFilters ?? filters,
      })

      return {
        facets,
        results: productCardsAndPushes ?? [],
        pageInfo: productsPageInfo ?? DEFAULT_PAGE_INFO,
        currentPage,
      }
    },
    paginationOptions: {
      hitsPerPage: PRODUCTS_PER_PAGE,
      getNextPageParam: (response) => {
        const { pageInfo, currentPage } = response ?? {}

        return pageInfo?.hasNextPage ? { after: pageInfo?.endCursor, pageNumber: currentPage + 1 } : undefined
      },
      getPreviousPageParam: (response) => {
        const { pageInfo, currentPage } = response ?? {}

        return pageInfo?.hasPreviousPage ? { before: pageInfo?.startCursor, pageNumber: currentPage - 1 } : undefined
      },
    },
    queryOptions: {
      enabled: true,
      keepPreviousData: true,
      refetchOnMount: true,
      staleTime: Infinity,
      ...(initialData &&
        shouldDisplayInitialData && {
          initialData: {
            pages: [initialData],
            pageParams: [computedPageParam],
          },
        }),
    },
  })
}
